import React, { useEffect } from 'react';
import dayjs from 'dayjs';
import { useSpinradContext } from '../../context/SpinradContext';
import { useTaskForm } from '../../../../hooks/useTaskForm';
import { Status } from '../../../TodoPage';
import {
  Actions,
  Box,
  CancelBtn,
  DateInput,
  ErrorHint,
  FieldGroup,
  FieldLabel,
  FieldRow,
  FormBody,
  Overlay,
  SaveBtn,
  SegmentGroup,
  SegmentOption,
  SelectField,
  TextInput,
  Title,
} from './styles';

const effortImpactOptions = ['Sehr Gering', 'Gering', 'Hoch', 'Sehr Hoch'];
const statusOptions: { label: string; value: Status }[] = [
  { label: '🕒 Nicht Begonnen', value: 'Nicht Begonnen' },
  { label: '🔄 Laufend', value: 'Laufend' },
  { label: '✔️ Erledigt', value: 'Erledigt' },
];

const TodoCreateModal: React.FC = () => {
  const {
    todoCreateOpen,
    closeTodoCreate,
    activeCard,
    markTodoCreated,
    switchView,
    showToast,
  } = useSpinradContext();

  const {
    task,
    required,
    companiesList,
    usergroupsList,
    filteredUserList,
    hideCompany,
    handleChange,
    handleChangeAssign,
    handleChangeCompany,
    handleChangeUsergroup,
    reset,
    submit,
  } = useTaskForm();

  useEffect(() => {
    if (todoCreateOpen) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todoCreateOpen]);

  const handleSave = (): void => {
    submit().then(savedTask => {
      if (!savedTask) return;

      closeTodoCreate();
      if (activeCard) markTodoCreated(activeCard.id);
      switchView('todo');
      showToast('Aufgabe erstellt');
    });
  };

  return (
    <Overlay
      $open={todoCreateOpen}
      onClick={e => {
        if (e.target === e.currentTarget) closeTodoCreate();
      }}
    >
      <Box>
        <Title>Neue Aufgabe</Title>

        <FormBody>
          <FieldGroup>
            <FieldLabel htmlFor="spinrad-task-title">Kurztitel</FieldLabel>
            <TextInput
              id="spinrad-task-title"
              type="text"
              maxLength={20}
              value={task?.shortTitle ?? ''}
              $error={!!required.shortTitle}
              onChange={e => handleChange('shortTitle', e.target.value)}
            />
            {required.shortTitle && <ErrorHint>Pflichtfeld</ErrorHint>}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="spinrad-task-description">Thema</FieldLabel>
            <TextInput
              id="spinrad-task-description"
              type="text"
              maxLength={5000}
              value={task?.description ?? ''}
              $error={!!required.description}
              onChange={e => handleChange('description', e.target.value)}
            />
            {required.description && <ErrorHint>Pflichtfeld</ErrorHint>}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="spinrad-task-goal">Ziel</FieldLabel>
            <TextInput
              id="spinrad-task-goal"
              type="text"
              maxLength={200}
              value={task?.goal ?? ''}
              onChange={e => handleChange('goal', e.target.value)}
            />
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="spinrad-task-due">Fällig</FieldLabel>
            <DateInput
              id="spinrad-task-due"
              type="date"
              value={dayjs(task?.due || new Date()).format('YYYY-MM-DD')}
              onChange={e =>
                handleChange(
                  'due',
                  e.target.value ? dayjs(e.target.value).toDate() : new Date(),
                )
              }
            />
          </FieldGroup>

          <FieldRow>
            {!hideCompany && (
              <FieldGroup>
                <FieldLabel htmlFor="spinrad-task-company">Company</FieldLabel>
                <SelectField
                  id="spinrad-task-company"
                  $error={!!required.companyId}
                  value={task?.companyId ?? ''}
                  onChange={e => handleChangeCompany(Number(e.target.value))}
                >
                  <option value="" disabled>
                    Bitte wählen...
                  </option>
                  {companiesList.map(company => (
                    <option key={company.companyId} value={company.companyId}>
                      {company.name}
                    </option>
                  ))}
                </SelectField>
                {required.companyId && <ErrorHint>Pflichtfeld</ErrorHint>}
              </FieldGroup>
            )}

            <FieldGroup>
              <FieldLabel htmlFor="spinrad-task-usergroup">
                Organisationseinheit
              </FieldLabel>
              <SelectField
                id="spinrad-task-usergroup"
                $error={!!required.userGroups}
                disabled={task?.companyId === undefined}
                value={task?.usergroupId || ''}
                onChange={e => handleChangeUsergroup(Number(e.target.value))}
              >
                <option value="" disabled>
                  Bitte wählen...
                </option>
                {usergroupsList.map(usergroup => (
                  <option
                    key={usergroup.usergroupId}
                    value={usergroup.usergroupId}
                  >
                    {usergroup.name}
                  </option>
                ))}
              </SelectField>
              {required.userGroups && <ErrorHint>Pflichtfeld</ErrorHint>}
            </FieldGroup>
          </FieldRow>

          <FieldGroup>
            <FieldLabel htmlFor="spinrad-task-assign">
              Verantwortlich
            </FieldLabel>
            <SelectField
              id="spinrad-task-assign"
              $error={!!required.assign}
              disabled={!task?.usergroupId}
              value={
                filteredUserList.length > 0 ? (task?.assign?.email ?? '') : ''
              }
              onChange={e => handleChangeAssign(e.target.value)}
            >
              <option value="" disabled>
                Bitte wählen...
              </option>
              {filteredUserList.map(user => (
                <option key={user.email} value={user.email}>
                  {user.name}
                </option>
              ))}
            </SelectField>
            {required.assign && <ErrorHint>Pflichtfeld</ErrorHint>}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>Aufwand</FieldLabel>
            <SegmentGroup>
              {effortImpactOptions.map(option => (
                <SegmentOption
                  key={option}
                  type="button"
                  $active={(task?.effort ?? 'Sehr Gering') === option}
                  onClick={() => handleChange('effort', option)}
                >
                  {option}
                </SegmentOption>
              ))}
            </SegmentGroup>
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>Impact</FieldLabel>
            <SegmentGroup>
              {effortImpactOptions.map(option => (
                <SegmentOption
                  key={option}
                  type="button"
                  $active={(task?.impact ?? 'Sehr Gering') === option}
                  onClick={() => handleChange('impact', option)}
                >
                  {option}
                </SegmentOption>
              ))}
            </SegmentGroup>
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>Status</FieldLabel>
            <SegmentGroup>
              {statusOptions.map(option => (
                <SegmentOption
                  key={option.value}
                  type="button"
                  $active={(task?.status ?? 'Nicht Begonnen') === option.value}
                  onClick={() => handleChange('status', option.value)}
                >
                  {option.label}
                </SegmentOption>
              ))}
            </SegmentGroup>
          </FieldGroup>
        </FormBody>

        <Actions>
          <CancelBtn type="button" onClick={closeTodoCreate}>
            Abbrechen
          </CancelBtn>
          <SaveBtn type="button" onClick={handleSave}>
            Erstellen
          </SaveBtn>
        </Actions>
      </Box>
    </Overlay>
  );
};

export default TodoCreateModal;
